// ===========================================
// Start week select — "Starting week" for an enrolment
// ===========================================
// A student can join a class part-way through its term. The options run from the CLASS's first
// week to its last (`lessonWeekWindow`), never the whole term, and never a hand-rolled week count
// (`termWeekCount`, #169). The default option is the class's own start and means "send nothing":
// the server stores an enrolment start equal to the class start as null.
// Labels are `Week 5 09/11/2026` via `formatTermWeekDate` (CLAUDE.md Critical Rule 7).

import { FormControl, InputLabel, Select, MenuItem, FormHelperText } from '@mui/material';
import { termWeekCount, lessonWeekWindow, formatTermWeekDate } from '../../../utils/dateUtils';

export interface StartWeekLesson {
  name?: string;
  startWeek?: number | null;
  endWeek?: number | null;
  dayOfWeek: number | null;
}

export interface StartWeekTerm {
  startDate: string;
  endDate: string;
  name?: string;
}

interface StartWeekSelectProps {
  lesson: StartWeekLesson;
  term: StartWeekTerm;
  /** null = the class's own start. */
  value: number | null;
  onChange: (startWeek: number | null) => void;
  disabled?: boolean;
  helperText?: string;
  id?: string;
}

export default function StartWeekSelect({
  lesson,
  term,
  value,
  onChange,
  disabled = false,
  helperText,
  id = 'enrol-start-week',
}: StartWeekSelectProps) {
  const { first, last } = lessonWeekWindow(lesson, termWeekCount(term.startDate, term.endDate));
  const label = (week: number) => formatTermWeekDate(term.startDate, week, lesson.dayOfWeek);
  const options = Array.from({ length: Math.max(0, last - first) }, (_, i) => first + 1 + i);
  // A stored start at/before the class start is the default; one past the window still renders.
  const shown = value === null || value <= first ? '' : String(value);
  const outside = value !== null && value > last;

  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel id={`${id}-label`} shrink>
        Starting week
      </InputLabel>
      <Select
        labelId={`${id}-label`}
        label="Starting week"
        notched
        displayEmpty
        value={shown}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        inputProps={{ 'data-testid': id }}
      >
        <MenuItem value="">{`Week ${first} (class start)`}</MenuItem>
        {options.map((w) => (
          <MenuItem key={w} value={String(w)}>
            {label(w)}
          </MenuItem>
        ))}
        {outside && <MenuItem value={String(value)}>{`Week ${value} (after the class's last week)`}</MenuItem>}
      </Select>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
